import React, { useState, useEffect, useCallback } from 'react';
import MonCompteurPage from './MonCompteur';
import { getOnboarding } from '../services/onboardingStore';
import API from '../services/api';
import Account from './Account';
import ConnexionCompteur from './ConnexionCompteur';

// Client commun : jeton ajouté et renouvelé automatiquement (services/api.js)

// ── Brand constants ──
const G = {
  bg:      '#06080C',
  card:    '#0D1117',
  lime:    '#C8F135',
  text:    '#EEF2FF',
  muted:   'rgba(238,242,255,0.45)',
  border:  'rgba(238,242,255,0.06)',
  green:   '#4ade80',
  amber:   '#f59e0b',
  title:   { fontFamily: "'Lexend', sans-serif" },
  body:    { fontFamily: "'Plus Jakarta Sans', sans-serif" },
  mono:    { fontFamily: "'IBM Plex Mono', monospace" },
};

// ── Reusable components ──
// Éclair du logo Nowatt, sans l'anneau ni le nom : bouton du menu sur mobile
const Bolt = ({ height = 24, color = G.lime }) => (
  <svg viewBox="0 0 190 514" height={height} width={height * 190 / 514} aria-hidden="true" style={{ display: 'block' }}>
    <polygon points="32,27 174,0 114,224 190,208 62,514 88,284 0,304" fill={color} />
  </svg>
);

// Icônes au trait, sobres, dans le style de l'app
const ICON_PATHS = {
  accueil:     ['M3 10.5 12 3l9 7.5', 'M5 9v11a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1V9'],
  comparateur: ['M4 7h14', 'M15 4l3 3-3 3', 'M20 17H6', 'M9 14l-3 3 3 3'],
  compteur:       ['M4.5 17a8 8 0 1 1 15 0', 'M12 17l3.5-4.5', 'M12 17h.01'],
  compte:      ['M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8z', 'M4.5 21a7.5 7.5 0 0 1 15 0'],
  fermer:      ['M6 6l12 12', 'M18 6 6 18'],
};
const Icon = ({ name, size = 20, color = 'currentColor' }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ display: 'block', flexShrink: 0 }}>
    {ICON_PATHS[name].map((d) => <path key={d} d={d} />)}
  </svg>
);

const Card = ({ children, style, onClick }) => (
  <div onClick={onClick} style={{
    background: G.card, borderRadius: 14, padding: 18,
    border: `1px solid ${G.border}`, ...style,
  }}>{children}</div>
);

const Badge = ({ children, color = G.lime }) => (
  <span style={{
    background: `${color}18`, color,
    fontSize: '0.7rem', padding: '3px 10px', borderRadius: 20,
    ...G.body, fontWeight: 600,
  }}>{children}</span>
);

// ── Colonne de droite (ordinateur) : uniquement des chiffres réels, issus de la recommandation ──
function SideSummary({ reco }) {
  const cur  = reco?.current_cost;
  const best = reco?.best_offer?.annual_cost;
  if (!(cur > 0 && best > 0)) return null;
  const rows = [['Actuel', cur / 12, G.amber, G.text], ['Meilleure offre', best / 12, G.green, G.green]];
  const max  = Math.max(cur, best) / 12;
  return (
    <div style={{ width: 260, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <Card style={{ padding: 14 }}>
        <p style={{ margin: '0 0 10px', fontSize: '0.72rem', color: G.muted, ...G.body }}>Comparaison mensuelle</p>
        {rows.map(([label, val, bar, color]) => (
          <div key={label} style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
              <span style={{ fontSize: '0.7rem', color: G.muted, ...G.body }}>{label}</span>
              <span style={{ fontSize: '0.72rem', fontWeight: 600, ...G.mono, color }}>{val.toFixed(2).replace('.', ',')} €</span>
            </div>
            <div style={{ height: 4, background: G.bg, borderRadius: 3 }}>
              <div style={{ width: `${(val / max) * 100}%`, height: '100%', borderRadius: 3, background: bar }} />
            </div>
          </div>
        ))}
        <p style={{ margin: '8px 0 0', fontSize: '0.66rem', color: G.muted, lineHeight: 1.5, ...G.body }}>Coût estimé par mois, abonnement compris.</p>
      </Card>
    </div>
  );
}

// ── Page Accueil ──
function PageAccueil({ reco, loading, handleSwitch, isDesktop }) {
  // Tous les montants viennent du serveur. Sans réponse, on affiche « — », jamais un chiffre inventé.
  const hasReco        = !!(reco && reco.best_offer);
  const savings        = hasReco ? Math.round(reco.annual_savings ?? Math.max(0, reco.current_cost - reco.best_offer.annual_cost)) : 0;
  const savingsMonthly = Math.round(savings / 12);
  const pctSaving      = hasReco && reco.current_cost > 0
    ? Math.max(0, Math.round((1 - reco.best_offer.annual_cost / reco.current_cost) * 100))
    : 0;
  const frDate         = (iso) => iso ? iso.slice(0, 10).split('-').reverse().join('/') : null;
  const basis          = reco?.basis;
  const unlocked       = !!reco?.unlocked;
  const best           = reco?.best_offer;

  const rowStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 };
  const labelStyle = { fontSize: '0.78rem', color: G.muted, ...G.body };
  const valStyle   = { fontSize: '0.88rem', fontWeight: 600, ...G.mono, color: G.text };

  return (
    <div>
      <h2 style={{ margin: '0 0 4px', fontSize: '1.3rem', ...G.title, fontWeight: 700 }}>Bonjour 👋</h2>
      <p style={{ margin: '0 0 20px', color: G.muted, fontSize: '0.82rem', ...G.body }}>Voici votre résumé énergétique</p>

      {/* ── Deux cards principales ── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isDesktop ? '1fr 1fr' : '1fr',
        gap: 12,
        marginBottom: 12,
      }}>

        {/* GAUCHE — Offre actuelle */}
        <Card style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <span style={{ fontSize: '0.7rem', color: G.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', ...G.body }}>
            Offre actuelle
          </span>

          {/* Fournisseur */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 38, height: 38, borderRadius: 9, background: 'rgba(238,242,255,0.06)', border: `1px solid ${G.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700, color: G.muted, ...G.mono }}>
              {(reco?.current_supplier || '?').slice(0, 3).toUpperCase()}
            </div>
            <div>
              <p style={{ margin: 0, fontWeight: 600, fontSize: '0.95rem', ...G.title }}>{reco?.current_supplier || 'Fournisseur non renseigné'}</p>
              <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: G.muted, ...G.body }}>Coût estimé au tarif réglementé</p>
            </div>
          </div>

          {/* Détails */}
          <div style={{ borderTop: `1px solid ${G.border}`, paddingTop: 14 }}>
            <div style={rowStyle}>
              <span style={labelStyle}>Prix/kWh</span>
              <span style={valStyle}>{(reco?.current_price_per_kwh ?? 0.1985).toFixed(4).replace('.', ',')} €</span>
            </div>
            <div style={rowStyle}>
              <span style={labelStyle}>Coût annuel</span>
              <span style={{ ...valStyle, color: G.text }}>{reco ? `${Math.round(reco.current_cost)} €` : '—'}</span>
            </div>
            <div style={rowStyle}>
              <span style={labelStyle}>Consommation</span>
              <span style={valStyle}>{reco?.annual_kwh ? `${reco.annual_kwh} kWh` : '—'}</span>
            </div>
            <div style={{ ...rowStyle, marginBottom: 0 }}>
              <span style={labelStyle}>Abonnement</span>
              <span style={valStyle}>{(reco?.current_monthly_subscription ?? 19.88).toFixed(2).replace('.', ',')} €/mois</span>
            </div>
          </div>

          {/* Statut */}
          <div style={{ borderTop: `1px solid ${G.border}`, paddingTop: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 7, height: 7, borderRadius: '50%', background: G.amber, flexShrink: 0 }} />
            <span style={{ fontSize: '0.72rem', color: G.muted, ...G.body }}>{!hasReco ? 'Analyse indisponible pour le moment' : savings > 0 ? 'Une offre moins chère existe' : 'Aucune offre moins chère trouvée'}</span>
          </div>
        </Card>

        {/* DROITE — Meilleure offre */}
        <Card style={{ padding: 20, border: `1px solid ${G.green}30`, display: 'flex', flexDirection: 'column', gap: 14, background: `linear-gradient(135deg, ${G.card}, rgba(74,222,128,0.04))` }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '0.7rem', color: G.green, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', ...G.body }}>
              Meilleure offre détectée
            </span>
            {hasReco && pctSaving > 0 && <Badge color={G.green}>−{pctSaving}%</Badge>}
          </div>

          {/* Fournisseur */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 38, height: 38, borderRadius: 9, background: `${G.green}15`, border: `1px solid ${G.green}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: 700, color: G.green, ...G.mono }}>
              {unlocked ? (best?.supplier_name?.slice(0, 3).toUpperCase() || '—') : '🔒'}
            </div>
            <div>
              <p style={{ margin: 0, fontWeight: 600, fontSize: '0.95rem', ...G.title }}>{unlocked ? (best?.supplier_name || '—') : hasReco ? 'Fournisseur réservé aux abonnés' : '—'}</p>
              <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: G.muted, ...G.body }}>{unlocked ? (best?.offer_name || '') : hasReco ? 'Abonnez-vous pour découvrir son nom et son offre' : ''}</p>
            </div>
          </div>

          {/* Détails */}
          <div style={{ borderTop: `1px solid ${G.green}20`, paddingTop: 14 }}>
  <div style={rowStyle}>
    <span style={labelStyle}>Prix/kWh</span>
    <span style={{ ...valStyle, color: G.green }}>{!unlocked && hasReco ? '🔒' : best?.price_per_kwh ? `${best.price_per_kwh.toFixed(4).replace('.', ',')} €` : '—'}</span>
  </div>
  <div style={rowStyle}>
    <span style={labelStyle}>Coût annuel</span>
    <span style={{ ...valStyle, color: G.green }}>{reco?.best_offer ? `${Math.round(reco.best_offer.annual_cost)} €` : '—'}</span>
  </div>
  <div style={{ ...rowStyle, marginBottom: 0 }}>
    <span style={labelStyle}>Abonnement</span>
    <span style={{ ...valStyle, color: G.green }}>{!unlocked && hasReco ? '🔒' : best?.monthly_subscription ? `${best.monthly_subscription.toFixed(2).replace('.', ',')} €/mois` : '—'}</span>
  </div>
</div>

          {/* Économie */}
          <div style={{ background: `${G.green}12`, borderRadius: 10, padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <p style={{ margin: 0, fontSize: '0.7rem', color: G.muted, ...G.body }}>Économie estimée sur votre facture/an</p>
              <p style={{ margin: '2px 0 0', fontSize: '0.7rem', color: G.muted, ...G.body }}>{hasReco ? `soit ~${savingsMonthly}€/mois` : ''}</p>
            </div>
            <span style={{ fontSize: '2rem', fontWeight: 700, color: G.green, ...G.mono }}>{hasReco ? `${savings}€` : '—'}</span>
          </div>

          {hasReco && (
            <p style={{ margin: 0, fontSize: '0.66rem', lineHeight: 1.5, color: G.muted, ...G.body }}>
              Estimation pour un compteur {basis?.kva || 9} kVA en option Base, comparée au tarif réglementé d'EDF.
              {basis?.offers_as_of ? ` Prix des offres relevés le ${frDate(basis.offers_as_of)}.` : ''} Le montant réel dépend de votre contrat et de votre consommation.
            </p>
          )}

          {/* CTA */}
          <button onClick={handleSwitch} style={{
            background: G.lime, color: G.bg, border: 'none', borderRadius: 10,
            padding: '12px', fontSize: '0.88rem', fontWeight: 700, cursor: 'pointer',
            ...G.title, marginTop: 'auto', display: unlocked ? 'none' : 'block',
          }}>
            Voir le fournisseur — 5,99€/mois
          </button>
        </Card>
      </div>

      {/* ── Stats légères ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
        {[
          { label: 'Offres comparées',   value: reco?.all_offers?.length || '—',    color: G.text },
          { label: 'Économie mensuelle', value: hasReco ? `${savingsMonthly}€` : '—',  color: G.green },
          { label: 'Prix relevés le',    value: frDate(basis?.offers_as_of) || '—',  color: G.text },
        ].map(s => (
          <Card key={s.label} style={{ padding: '12px 14px' }}>
            <p style={{ margin: '0 0 4px', fontSize: '0.7rem', color: G.muted, ...G.body }}>{s.label}</p>
            <p style={{ margin: 0, fontSize: '1.2rem', fontWeight: 600, color: s.color, ...G.mono }}>{s.value}</p>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ════════════════════════════════════════
//  MAIN DASHBOARD
// ════════════════════════════════════════
export default function Dashboard({ user, onLogout, onAccountDeleted, onRestartOnboarding }) {
  const userId = user?.user_id;
  const hasConsumption = !!getOnboarding(userId)?.kwh;
  const [page,        setPage]        = useState(() => new URLSearchParams(window.location.search).get('page') || 'accueil');
  const [reco,        setReco]        = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [subStatus,   setSubStatus]   = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isDesktop,   setIsDesktop]   = useState(window.innerWidth >= 900);

  useEffect(() => {
    const handler = () => setIsDesktop(window.innerWidth >= 900);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const getToken = useCallback(async () => {
    try {
      const direct = localStorage.getItem('nowatt_token');
      if (direct) return direct;
      const raw = localStorage.getItem('sb-wtlstbgmjjkcklrrsydp-auth-token');
      if (raw) { const p = JSON.parse(raw); return p?.access_token || null; }
    } catch {}
    return null;
  }, []);

  const fetchReco = useCallback(async (kwh) => {
    setLoading(true);
    try {
      const token = await getToken();
      if (!token) { setLoading(false); return; }
      const kwh2 = kwh || 2500;
      const res = await API.post('/recommendation/best-offer', { annual_kwh: kwh2 }, { headers: { Authorization: `Bearer ${token}` } });
      const d = res.data;
      d.current_cost     = d.current_annual_cost;
      d.current_supplier = getOnboarding(userId)?.supplier || null;
      // Les coûts annuels sont calculés par le serveur. Sans abonnement, il n'envoie ni le nom
      // du fournisseur ni le détail des offres : d.unlocked dit si le compte y a droit.
      setReco(d);
    } catch (e) { console.error('reco error:', e); }
    setLoading(false);
  }, [getToken, userId]);

  useEffect(() => {
    // Réponses données par CE compte pendant l'onboarding. Sans consommation saisie,
    // on ne calcule rien : pas de chiffre par défaut présenté comme le sien.
    const kwh = getOnboarding(userId)?.kwh;
    if (kwh) fetchReco(kwh); else setLoading(false);
  }, []); // eslint-disable-line

  // Statut d'abonnement du compte, demandé au serveur
  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        if (!token) return;
        const res = await API.get('/stripe/status', { headers: { Authorization: `Bearer ${token}` } });
        setSubStatus(res.data || null);
      } catch { /* statut inconnu : on considère le compte comme non abonné */ }
    })();
  }, [getToken]);

  useEffect(() => {
    const titles = { accueil: 'Accueil', comparateur: 'Comparateur', compteur: 'Mon compteur', compte: 'Mon compte' };
    document.title = (titles[page] || 'Accueil') + ' — Nowatt';
  }, [page]);

  const navigateTo = (p) => { setPage(p); setSidebarOpen(false); };

  // Parcours de connexion du compteur (Data Connect). Au retour d'Enedis, le serveur renvoie
  // vers l'app avec ?compteur=connecte|refuse|erreur : on affiche alors l'écran de résultat.
  const [connexion, setConnexion] = useState(() => {
    const r = new URLSearchParams(window.location.search).get('compteur');
    return ['connecte', 'refuse', 'erreur'].includes(r) ? { resultat: r } : null;
  });
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('compteur')) return;
    params.delete('compteur');
    const q = params.toString();
    window.history.replaceState(null, '', window.location.pathname + (q ? `?${q}` : '') + window.location.hash);
  }, []);
  const ouvrirConnexion = () => setConnexion({ resultat: null });
  const fermerConnexion = () => {
    const connecte = connexion?.resultat === 'connecte';
    setConnexion(null);
    if (connecte) navigateTo('compteur');
  };

  // Touche Échap : ferme le menu mobile
  useEffect(() => {
    if (!sidebarOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setSidebarOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sidebarOpen]);

  const handleLogout = () => {
    if (onLogout) { onLogout(); return; }
    localStorage.removeItem('nowatt_token');
    localStorage.removeItem('nowatt_user');
    window.location.reload();
  };

  const handleSwitch = async () => {
    try {
      const token = await getToken();
      const res = await API.post('/stripe/create-checkout-session', {}, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data?.url) window.location.href = res.data.url;
    } catch (e) { console.error(e); }
  };

  const navItems = [
    { id: 'accueil',     label: 'Accueil'      },
    { id: 'comparateur', label: 'Comparateur'  },
    { id: 'compteur',    label: 'Mon compteur' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: G.bg, color: G.text, display: 'flex', flexDirection: 'column', ...G.body }}>
      {connexion && <ConnexionCompteur resultat={connexion.resultat} onClose={fermerConnexion} />}

      {/* TOP BAR */}
      <div style={{
        height: 54, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 16px', borderBottom: `1px solid ${G.border}`,
        background: 'rgba(13,17,23,0.95)', backdropFilter: 'blur(10px)',
        position: 'sticky', top: 0, zIndex: 50,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Sur mobile, le logo est le bouton qui ouvre le menu */}
          {isDesktop ? (
            <img src="/Logo_nowatt_Black.png" alt="Nowatt" style={{ width: 46, height: 46, objectFit: 'contain', mixBlendMode: 'screen' }} />
          ) : (
            <button onClick={() => setSidebarOpen(true)} aria-label="Ouvrir le menu" aria-expanded={sidebarOpen} style={{ background: 'none', border: 'none', cursor: 'pointer', width: 44, height: 44, marginLeft: -10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Bolt height={26} />
            </button>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', height: 'calc(100vh - 54px)' }}>

        {/* SIDEBAR GAUCHE DESKTOP */}
        {isDesktop && (
          <div style={{ width: 200, flexShrink: 0, borderRight: `1px solid ${G.border}`, padding: '16px 10px', display: 'flex', flexDirection: 'column', gap: 4, background: 'rgba(13,17,23,0.5)', overflowY: 'auto' }}>
            {navItems.map(item => (
              <button key={item.id} onClick={() => navigateTo(item.id)} style={{
                background: page === item.id ? `${G.lime}12` : 'transparent',
                color: page === item.id ? G.lime : G.muted, border: 'none', borderRadius: 10,
                padding: '10px 14px', fontSize: '0.82rem', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left',
                ...G.title, fontWeight: page === item.id ? 600 : 400, transition: 'all 0.15s',
              }}>
                <Icon name={item.id} />{item.label}
              </button>
            ))}
            <div style={{ flex: 1 }} />
            <button onClick={() => navigateTo('compte')} style={{
              background: page === 'compte' ? `${G.lime}12` : 'transparent',
              color: page === 'compte' ? G.lime : G.muted, border: 'none', borderRadius: 10,
              padding: '10px 14px', fontSize: '0.82rem', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left',
              ...G.title, fontWeight: page === 'compte' ? 600 : 400, marginTop: 4,
            }}>
              <Icon name="compte" />Mon compte
            </button>
            <button onClick={handleLogout} style={{ background: 'none', border: '1px solid rgba(255,68,68,0.25)', color: '#ff4444', borderRadius: 8, padding: '8px 12px', fontSize: '0.75rem', cursor: 'pointer', marginTop: 4, ...G.body }}>
              Déconnexion
            </button>
          </div>
        )}

        {/* MOBILE SIDEBAR OVERLAY */}
        {!isDesktop && sidebarOpen && (
          <>
            <style>{`
              @keyframes nw-fade { from { opacity: 0 } to { opacity: 1 } }
              @keyframes nw-slide { from { transform: translateX(-100%) } to { transform: none } }
            `}</style>
            <div onClick={() => setSidebarOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 60, animation: 'nw-fade .2s ease' }} />
            <nav aria-label="Menu" style={{ position: 'fixed', top: 0, left: 0, bottom: 0, width: 264, maxWidth: '82vw', background: G.bg, borderRight: `1px solid ${G.border}`, zIndex: 70, padding: '6px 12px 20px', display: 'flex', flexDirection: 'column', gap: 4, overflowY: 'auto', animation: 'nw-slide .22s ease' }}>
              {/* En-tête du menu : la marque et un bouton pour fermer */}
              <div style={{ height: 48, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 2px 0 6px', marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Bolt height={22} />
                  <span style={{ ...G.title, fontWeight: 700, fontSize: '1.05rem' }}><span style={{ color: G.lime }}>No</span>watt</span>
                </div>
                <button onClick={() => setSidebarOpen(false)} aria-label="Fermer le menu" style={{ background: 'none', border: 'none', color: G.muted, cursor: 'pointer', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon name="fermer" size={22} />
                </button>
              </div>
              {[...navItems, { id: 'compte', label: 'Mon compte' }].map(item => (
                <button key={item.id} onClick={() => navigateTo(item.id)} style={{
                  background: page === item.id ? `${G.lime}12` : 'transparent',
                  color: page === item.id ? G.lime : G.muted, border: 'none', borderRadius: 10,
                  padding: '12px 14px', fontSize: '0.9rem', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left',
                  ...G.title, fontWeight: page === item.id ? 600 : 400,
                }}>
                  <Icon name={item.id} />{item.label}
                </button>
              ))}
              <div style={{ height: 1, background: G.border, margin: '12px 6px' }} />
              <button onClick={handleLogout} style={{ background: 'none', border: '1px solid rgba(255,68,68,0.25)', color: '#ff4444', borderRadius: 8, padding: '10px 12px', fontSize: '0.8rem', cursor: 'pointer', margin: '0 6px', ...G.body }}>
                Déconnexion
              </button>
            </nav>
          </>
        )}

        {/* MAIN CONTENT */}
        <div style={{ flex: 1, overflow: 'auto', padding: isDesktop ? '20px 24px' : '16px 12px' }}>
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
              <div style={{ textAlign: 'center' }}>
                <img src="/favicon_nowatt.png" alt="" style={{ width: 40, height: 40, objectFit: 'contain', marginBottom: 12 }} />
                <p style={{ color: G.muted, fontSize: '0.85rem', ...G.body }}>Analyse en cours...</p>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 20 }}>
              <div style={{ flex: 1, minWidth: 0 }}>

                {/* ACCUEIL */}
                {page === 'accueil' && hasConsumption && (
                  <PageAccueil reco={reco} loading={loading} handleSwitch={handleSwitch} isDesktop={isDesktop} />
                )}
                {/* Aucune consommation saisie : on invite à la renseigner, sans afficher de chiffres */}
                {page === 'accueil' && !hasConsumption && (
                  <div>
                    <h2 style={{ margin: '0 0 4px', fontSize: '1.3rem', ...G.title, fontWeight: 700 }}>Bonjour 👋</h2>
                    <p style={{ margin: '0 0 20px', color: G.muted, fontSize: '0.82rem', ...G.body }}>Il nous manque une petite info pour calculer votre économie.</p>
                    <Card style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 14 }}>
                      <p style={{ margin: 0, fontSize: '1rem', fontWeight: 600, ...G.title }}>Indiquez votre consommation</p>
                      <p style={{ margin: 0, fontSize: '0.84rem', lineHeight: 1.55, color: G.muted, ...G.body }}>
                        Deux questions suffisent : votre fournisseur actuel et votre consommation annuelle. Nowatt compare ensuite les offres et vous montre ce que vous pouvez économiser.
                      </p>
                      <button onClick={onRestartOnboarding} style={{ background: G.lime, color: G.bg, border: 'none', borderRadius: 10, padding: '12px', fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer', ...G.title }}>
                        Renseigner ma consommation
                      </button>
                    </Card>
                  </div>
                )}

                {/* COMPARATEUR */}
                {page === 'comparateur' && (
                  <div>
                    <h2 style={{ margin: '0 0 4px', fontSize: '1.3rem', ...G.title, fontWeight: 700 }}>Comparateur d'offres</h2>
                    <p style={{ margin: '0 0 20px', color: G.muted, fontSize: '0.82rem', ...G.body }}>
                      {reco ? `${reco.all_offers.length} offres analysées pour votre profil` : hasConsumption ? 'Chargement...' : 'Renseignez votre consommation depuis l\'accueil pour comparer les offres.'}
                    </p>
                    {reco?.all_offers?.sort((a, b) => a.annual_cost - b.annual_cost).map((offer, i) => {
                      const isBest = i === 0;
                      const diff   = reco.current_cost - offer.annual_cost;
                      return (
                        <Card key={i} style={{ marginBottom: 10, padding: 14, border: isBest ? `1px solid ${G.green}30` : `1px solid ${G.border}`, background: isBest ? `${G.green}05` : G.card }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                <p style={{ margin: 0, fontWeight: 600, fontSize: '0.9rem', ...G.title }}>{offer.locked ? `🔒 Offre n° ${i + 1}` : offer.supplier_name}</p>
                                {isBest && <Badge color={G.green}>Meilleure offre</Badge>}
                              </div>
                              <p style={{ margin: 0, fontSize: '0.75rem', color: G.muted, ...G.body }}>{offer.locked ? 'Fournisseur et tarifs réservés aux abonnés' : offer.offer_name}</p>
                              {!offer.locked && (
                                <p style={{ margin: '4px 0 0', fontSize: '0.72rem', color: G.muted, ...G.mono }}>
                                  {offer.price_per_kwh?.toFixed(4)}€/kWh · Abo: {offer.monthly_subscription?.toFixed(2)}€/mois
                                </p>
                              )}
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <p style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: isBest ? G.green : G.text, ...G.mono }}>
                                {Math.round(offer.annual_cost)}€<span style={{ fontSize: '0.7rem', color: G.muted }}>/an</span>
                              </p>
                              {diff > 0 && <p style={{ margin: '2px 0 0', fontSize: '0.72rem', color: G.green, ...G.body }}>-{Math.round(diff)}€ vs actuel</p>}
                            </div>
                          </div>
                        </Card>
                      );
                    })}
                    <div style={{ textAlign: 'center', marginTop: 16, display: reco?.unlocked ? 'none' : 'block' }}>
                      <button onClick={handleSwitch} style={{ background: G.lime, color: G.bg, border: 'none', borderRadius: 12, padding: '12px 28px', fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer', ...G.title }}>
                        Voir les fournisseurs — 5,99€/mois
                      </button>
                    </div>
                  </div>
                )}

                {/* MON COMPTEUR */}
                {page === 'compteur' && <MonCompteurPage reco={reco} onNavigate={navigateTo} onConnectMeter={ouvrirConnexion} />}

                {/* MON COMPTE */}
                {page === 'compte' && (
                  <Account onConnectMeter={ouvrirConnexion} reco={reco} subStatus={subStatus} setSubStatus={setSubStatus} onSubscribe={handleSwitch}
                    onEditEnergy={onRestartOnboarding}
                    onAccountDeleted={() => (onAccountDeleted ? onAccountDeleted() : handleLogout())} isDesktop={isDesktop} />
                )}
              </div>

              {/* Colonne de droite, ordinateur uniquement : comparaison chiffrée réelle */}
              {isDesktop && page !== 'compteur' && page !== 'compte' && <SideSummary reco={reco} />}
            </div>
          )}
        </div>
      </div>

      {/* BOTTOM NAV MOBILE */}
      {!isDesktop && (
        <div style={{ flexShrink: 0, display: 'flex', background: 'rgba(13,17,23,0.98)', borderTop: `1px solid ${G.border}`, padding: '8px 0 4px' }}>
          {navItems.map(item => (
            <button key={item.id} onClick={() => navigateTo(item.id)} style={{ flex: 1, background: 'none', border: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, padding: '6px 0', color: page === item.id ? G.lime : 'rgba(238,242,255,0.35)', ...G.body }}>
              <Icon name={item.id} size={22} />
              <span style={{ fontSize: '0.65rem', fontWeight: page === item.id ? 600 : 400 }}>{item.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
